import axios from 'axios';
import { buildUrl } from 'Util/FetchUtils';
import { TRANSACTION_ENTITY_URL } from 'Constants';
import { PAYMENT_REQUEST_TIMEOUT_MS } from './PaymentService';

export interface PaymentTabPricesDto {
	totalPrice: number;
	creditCardSurcharge: number;
}

export async function getPaymentTabTotalAmount(
	isEventBooking: boolean,
	inCheckIn: boolean,
	transactionId?: string,
): Promise<PaymentTabPricesDto | null> {
	if (transactionId == null) {
		return null;
	}

	const url = buildUrl(`${TRANSACTION_ENTITY_URL}/total-price`, {
		id: transactionId,
		isEventBooking: isEventBooking ? 'true' : 'false',
		inCheckIn: inCheckIn ? 'true' : 'false',
	});

	// Bounded: the payment screen blocks on this, so an unanswered request is a stuck screen.
	const result = await axios.get(url, { timeout: PAYMENT_REQUEST_TIMEOUT_MS });
	return result.data as PaymentTabPricesDto;
}

export interface TransactionFeesDto {
	transactionId: string;
	cancellationFee: number;
	giftCertificateValue: number;
	totalCancellationAmount: number;
	recommendedCancellationFee: number;
	recommendedGiftCertificateValue: number;
	voucherUsedAmount: number;
	// True when nothing was paid (billed on an invoice, or still owed): edit the fee only (no
	// voucher), bounded by the booking fare below.
	isFeeOnly: boolean;
	bookingTotalCost: number;
	// True when the customer can see booking notes (they're an invoiced user). Used to warn staff
	// that the note they enter will be visible to the customer.
	customerCanViewNotes: boolean;
	// True for an alteration rather than a cancellation. A cancellation splits a fixed refund pool
	// between fee and voucher, so the two fields are pinned to a constant sum. An alteration has no
	// pool: the fee is edited freely up to bookingTotalCost and the voucher is derived from it.
	isAlteration: boolean;

	// The ceiling for an alteration's fee: what its fees would have been with nobody intervening.
	// Zero for a cancellation, whose refund pool bounds it instead.
	maximumFee: number;

	// What to call the amount being edited, from the fee rows actually in scope — "Alteration fee",
	// "Trailer removal fee", the tenant's wording for the check-in forfeit fee, or "Fees" for a mix.
	feeLabel: string;
}

export async function getTransactionFees(transactionId: string): Promise<TransactionFeesDto> {
	const response = await axios.get(`${TRANSACTION_ENTITY_URL}/get-fees/${transactionId}`);
	return response.data as TransactionFeesDto;
}

export async function updateTransactionFees(
	transactionId: string,
	cancellationFee: number,
	giftCertificateValue: number,
	note: string,
) {
	return axios.put(`${TRANSACTION_ENTITY_URL}/update-fees/${transactionId}`, {
		cancellationFee,
		giftCertificateValue,
		note,
	});
}
